import { createColumnHelper } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { SkuType, type SkuAssemblyComponentDTO } from '@invenco/products-interface';

import { Button } from '@/components/core/button';
import { Card, CardAction, CardHeader } from '@/components/core/card';
import { DialogTrigger } from '@/components/core/dialog';
import {
  DataTable,
  DataTableCardBody,
  DataTableCardTitle,
  useDataTable,
} from '@/components/data-table';
import { FormDialog } from '@/components/forms/form-dialog';
import { formatNumber } from '@/shared/helpers';
import type { Result } from '@/shared/helpers/Result';

import { SkuBadge } from '../../components/sku-badge';
import { ComponentsManagementDialog } from './components-management-dialog';
import { SubstitutesManagementDialog } from './substitutes-management-dialog';

type Props = {
  components: Partial<SkuAssemblyComponentDTO>[];
  skuType: SkuType;
  title?: ReactNode;
  showTitle?: boolean;
  isLoading?: boolean;
  onUpdateComponents?: (
    components: Partial<SkuAssemblyComponentDTO>[],
  ) => Promise<Result> | Promise<void> | void;
};

type TableRow = {
  id: string;
  skuName?: string;
  skuType?: SkuType;
  skuDescription?: string | null;
  quantity?: number;
  priority?: number;
};

function SkuDescriptionCell({
  name,
  type,
  description,
}: {
  name?: string;
  type?: SkuType;
  description?: string | null;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <SkuBadge name={name ?? 'Unknown SKU'} type={type} />
      {description ? <span className="min-w-0 truncate text-sm">{description}</span> : null}
    </div>
  );
}

const columnHelper = createColumnHelper<TableRow>();

const skuDescriptionColumn = columnHelper.accessor('skuName', {
  header: 'SKU / Description',
  cell: ({ row }) => (
    <SkuDescriptionCell
      name={row.original.skuName}
      type={row.original.skuType}
      description={row.original.skuDescription}
    />
  ),
  meta: { truncate: true },
});

const bundleColumns = [
  skuDescriptionColumn,
  columnHelper.accessor('quantity', {
    header: 'Quantity',
    cell: ({ getValue }) => formatNumber(getValue() ?? 0),
    meta: { align: 'right' as const },
  }),
];

const mysteryColumns = [
  skuDescriptionColumn,
  columnHelper.accessor('quantity', {
    header: 'Qty per option',
    cell: ({ getValue }) => formatNumber(getValue() ?? 0),
    meta: { align: 'right' as const },
  }),
];

const aliasColumns = [
  skuDescriptionColumn,
  columnHelper.accessor('priority', {
    header: 'Priority',
    cell: ({ getValue }) => getValue() ?? 0,
    meta: { align: 'right' as const },
  }),
];

function getColumns(type: SkuType) {
  switch (type) {
    case SkuType.MYSTERY:
      return mysteryColumns;
    case SkuType.ALIAS:
      return aliasColumns;
    default:
      return bundleColumns;
  }
}

export function ComponentsTable({
  components,
  skuType,
  title = 'Components',
  showTitle = true,
  isLoading,
  onUpdateComponents,
}: Props) {
  const rows = useMemo<TableRow[]>(() => {
    if (skuType === SkuType.ALIAS) {
      return components
        .slice()
        .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
        .map((component) => ({
          id:
            component.id ??
            `${component.componentSku?.id ?? 'component'}-${component.priority ?? 0}`,
          skuName: component.componentSku?.name,
          skuType: component.componentSku?.type,
          skuDescription: component.componentSku?.description,
          priority: component.priority ?? 0,
        }));
    }
    return components.map((component) => ({
      id: component.id ?? `${component.componentSku?.id ?? 'component'}-${component.quantity ?? 0}`,
      skuName: component.componentSku?.name,
      skuType: component.componentSku?.type,
      skuDescription: component.componentSku?.description,
      quantity: component.quantity ?? 0,
    }));
  }, [components, skuType]);

  const columns = getColumns(skuType);

  const table = useDataTable({
    data: rows,
    columns,
    isLoading,
    initialState: { pagination: { pageIndex: 0, pageSize: 10 } },
    manualPagination: true,
    manualSorting: false,
  });

  return (
    <Card aria-label={typeof title === 'string' ? title : undefined}>
      {showTitle || onUpdateComponents ? (
        <CardHeader>
          {showTitle ? <DataTableCardTitle table={table}>{title}</DataTableCardTitle> : null}
          {onUpdateComponents ? (
            <CardAction>
              <FormDialog>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline">
                    Manage
                  </Button>
                </DialogTrigger>
                {skuType === SkuType.ALIAS ? (
                  <SubstitutesManagementDialog
                    components={components}
                    onSubmit={onUpdateComponents}
                  />
                ) : (
                  <ComponentsManagementDialog
                    components={components}
                    onSubmit={onUpdateComponents}
                  />
                )}
              </FormDialog>
            </CardAction>
          ) : null}
        </CardHeader>
      ) : null}
      <DataTableCardBody table={table}>
        <DataTable table={table} />
      </DataTableCardBody>
    </Card>
  );
}
